import { useEffect, useRef } from 'react'
import { Search, Filter } from 'lucide-react'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { StoreModelsLocationFilter } from './StoreModelsLocationFilter'
import {
  HierarchyFilter,
  type HierarchyFilterValue,
} from '@/components/AssortmentPlansGrid/HierarchyFilter'
import type { ModelType, ModelStatus } from '@/types'
import type { StoreModelLocationFilters } from '@/api/storeModels'

const MODEL_TYPE_OPTIONS: { value: ModelType | 'all'; label: string }[] = [
  { value: 'all', label: 'All Types' },
  { value: 'STATIC', label: 'Static' },
  { value: 'DYNAMIC', label: 'Dynamic' },
]

const STATUS_OPTIONS: { value: ModelStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'PROCESSING', label: 'Processing' },
  { value: 'ERROR', label: 'Error' },
  { value: 'PENDING', label: 'Pending' },
]

const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: '-created_at', label: 'Newest First' },
  { value: '+created_at', label: 'Oldest First' },
  { value: '+name', label: 'Name A-Z' },
  { value: '-name', label: 'Name Z-A' },
  { value: '-updated_at', label: 'Recently Updated' },
]

interface StoreModelsFiltersProps {
  searchInput: string
  onSearchChange: (value: string) => void
  modelType: ModelType | 'all'
  onModelTypeChange: (value: ModelType | 'all') => void
  status: ModelStatus | 'all'
  onStatusChange: (value: ModelStatus | 'all') => void
  locations: StoreModelLocationFilters
  onLocationsChange: (value: StoreModelLocationFilters) => void
  hierarchy: HierarchyFilterValue
  onHierarchyChange: (value: HierarchyFilterValue) => void
  sort: string
  onSortChange: (value: string) => void
}

export function StoreModelsFilters({
  searchInput,
  onSearchChange,
  modelType,
  onModelTypeChange,
  status,
  onStatusChange,
  locations,
  onLocationsChange,
  hierarchy,
  onHierarchyChange,
  sort,
  onSortChange,
}: StoreModelsFiltersProps) {
  const filtersRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const activeElement = document.activeElement
      if (
        !(activeElement instanceof HTMLElement) ||
        !filtersRef.current?.contains(activeElement) ||
        (event.target instanceof Node && activeElement.contains(event.target))
      ) {
        return
      }

      window.setTimeout(() => {
        if (
          document.activeElement === activeElement &&
          activeElement.getAttribute('aria-expanded') !== 'true'
        ) {
          activeElement.blur()
        }
      }, 0)
    }

    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [])

  return (
    <div
      ref={filtersRef}
      className="flex items-center gap-3 rounded-lg border border-border bg-card p-3"
    >
      {/* Search Input */}
      <div className="relative flex-1 max-w-[352px]">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search name, dept/class/subclass, location..."
          value={searchInput}
          onChange={e => onSearchChange(e.target.value)}
          className="pl-9 bg-muted/50"
        />
      </div>

      {/* Filter Icon */}
      <Filter className="h-6 w-6 text-muted-foreground" />

      {/* Model Type Filter */}
      <Select
        value={modelType}
        onValueChange={value => onModelTypeChange(value as ModelType | 'all')}
      >
        <SelectTrigger className="w-[140px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {MODEL_TYPE_OPTIONS.map(option => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Status Filter */}
      <Select
        value={status}
        onValueChange={value => onStatusChange(value as ModelStatus | 'all')}
      >
        <SelectTrigger className="w-[150px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {STATUS_OPTIONS.map(option => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Locations Filter */}
      <StoreModelsLocationFilter
        value={locations}
        onValueChange={onLocationsChange}
        className="w-[150px]"
      />

      <HierarchyFilter
        value={hierarchy}
        onValueChange={onHierarchyChange}
        className="w-[180px]"
      />

      {/* Sort */}
      <Select value={sort} onValueChange={onSortChange}>
        <SelectTrigger className="w-[170px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SORT_OPTIONS.map(option => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
